import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ErrorDialogProvider, ToastProvider } from '@dayon/design-system';

import '@dayon/design-system/tokens.css';
import '@dayon/design-system/base.css';
import '@dayon/design-system/components.css';

import { SessionProvider, useSession } from './lib/session.tsx';
import { SignIn } from './features/auth/SignIn.tsx';
import { AcceptInvite } from './features/auth/AcceptInvite.tsx';
import { ForgotPassword } from './features/auth/ForgotPassword.tsx';
import { ResetPassword } from './features/auth/ResetPassword.tsx';
import { Shell } from './features/shell/Shell.tsx';
import { ErrorBoundary } from './features/shell/ErrorBoundary.tsx';
import { ConsoleRoutes } from './routes.tsx';

function Routed() {
  const { session, loading } = useSession();

  if (loading) {
    return (
      <div className="shell-body">
        <div className="skeleton" style={{ height: '200px' }}>&nbsp;</div>
      </div>
    );
  }
  /*
   * One signed-out screen is not enough any more.
   *
   * An invited admin has no session by construction — that is what an invite
   * IS — so the acceptance screen has to render for somebody the console does
   * not know. It is matched on the path rather than mounted under the router
   * below, because everything down there assumes a session exists.
   *
   * Somebody who is ALREADY signed in and opens an invite link still gets this
   * screen, deliberately: the link is for a different person, and dropping them
   * onto the dashboard instead would leave the invited admin's link silently
   * consumed by nothing.
   */
  if (window.location.pathname === '/accept-invite') return <AcceptInvite />;
  /* The same reasoning one line up, for the same reason: an administrator
     recovering an account has no session by construction. Both are matched
     on the path rather than mounted under the router, where everything
     assumes a session exists — and both are deliberately OUTSIDE
     `ConsoleRoutes`, so they touch neither the nav test nor the twenty
     modules the product IA names. */
  if (window.location.pathname === '/forgot-password') return <ForgotPassword />;
  if (window.location.pathname === '/reset-password') return <ResetPassword />;
  if (!session) return <SignIn />;

  return (
    <Shell>
      {/* INSIDE the shell, not around it: a screen that throws should leave the
          sidebar, the brand scope and the account menu standing so the operator
          can navigate away or sign out. Wrapping the shell as well would take
          the navigation down with the screen. */}
      <ErrorBoundary>
        <ConsoleRoutes />
      </ErrorBoundary>
    </Shell>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorDialogProvider>
      <ToastProvider>
        <SessionProvider>
          <BrowserRouter>
            <Routed />
          </BrowserRouter>
        </SessionProvider>
      </ToastProvider>
    </ErrorDialogProvider>
  </StrictMode>,
);
